import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Images, Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import { VIEWS } from "../lib/data";
import { api, fileUrl } from "../lib/api";

const SLOTS = [
  ...VIEWS.map((v) => ({ key: v.key, title: v.title })),
];

export default function Upload() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const [photos, setPhotos] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const galleryRefs = useRef({});
  const cameraRefs = useRef({});

  useEffect(() => {
    if (!sid) return navigate("/intake");
    api.get(`/screenings/${sid}`).then((r) => {
      const p = r.data.photos || {};
      const map = {};
      Object.keys(p).forEach((k) => { map[k] = { url: fileUrl(`/api/files/${sid}/enhanced/${k}`) + `?t=${Date.now()}` }; });
      setPhotos(map);
    }).catch(() => {});
  }, [sid]);

  const upload = async (key, file) => {
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("view_type", key);
      fd.append("file", file, `${key}.jpg`);
      await api.post(`/screenings/${sid}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => ({ ...p, [key]: { url: URL.createObjectURL(file) } }));
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusyKey(null);
    }
  };

  const count = Object.keys(photos).length;

  return (
    <Shell header={<TopBar title="Upload your teeth photos" />}>
      <div className="px-6 py-5 flex flex-col gap-4 pb-28">
        <p className="text-sm text-muted-foreground">
          All slots are optional. Add the teeth photos you already have — for X-rays or OPG, use the AI IOPA / OPG scan instead.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {SLOTS.map((s) => {
            const done = photos[s.key];
            return (
              <div key={s.key} data-testid={`upload-slot-${s.key}`}
                className="relative rounded-2xl border border-border bg-card overflow-hidden aspect-[4/3] flex flex-col">
                {done ? (
                  <>
                    <img src={done.url} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></div>
                    <span className="absolute bottom-1.5 left-2 text-white text-[11px] font-semibold drop-shadow">{s.title}</span>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 p-2">
                    <span className="text-[12.5px] font-medium text-foreground text-center">{s.title}</span>
                    {busyKey === s.key ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (
                      <div className="flex gap-2">
                        <button data-testid={`upload-gallery-${s.key}`} onClick={() => galleryRefs.current[s.key]?.click()} className="tap w-9 h-9 rounded-full bg-accent flex items-center justify-center"><Images className="w-4 h-4 text-primary" /></button>
                        <button data-testid={`upload-camera-${s.key}`} onClick={() => cameraRefs.current[s.key]?.click()} className="tap w-9 h-9 rounded-full bg-accent flex items-center justify-center"><Camera className="w-4 h-4 text-primary" /></button>
                      </div>
                    )}
                  </div>
                )}
                <input ref={(el) => (galleryRefs.current[s.key] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(s.key, f); }} />
                <input ref={(el) => (cameraRefs.current[s.key] = el)} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(s.key, f); }} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-0 px-6 py-4 bg-background border-t border-border">
        <button data-testid="upload-analyze" onClick={() => navigate(`/addxray?sid=${sid}`)}
          className="tap w-full h-14 rounded-2xl brand-gradient text-white font-semibold text-base flex items-center justify-center gap-2">
          {count > 0 ? `Continue with ${count} image${count > 1 ? "s" : ""}` : "Continue without images"} <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </Shell>
  );
}
